import { useEffect, useLayoutEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { keypadDigitsToMinor } from "@shared/money";

/** Most digits a keypad entry holds (9 digits covers `MAX_MINOR`). */
const MAX_DIGITS = 9;

/** One more digit on the right, without leading zeros. Shared by the on-screen keys and the hardware keys. */
export function appendDigit(digits: string, digit: string): string {
  return (digits + digit).replace(/^0+(?=\d)/, "").slice(0, MAX_DIGITS);
}

/**
 * The only keyboard in the selling flow (U-004). Digits accumulate right-to-left as minor units;
 * `keypadDigitsToMinor` from @shared/money does the conversion so every screen agrees.
 * LOCAL FALLBACK for C1's `Keypad` primitive (D-C2-4) — same contract, so the swap is mechanical.
 */
export function Keypad(props: {
  digits: string;
  onDigits: (next: string) => void;
  /** Rendered under the keys, e.g. the tax note in Setup. */
  note?: string;
  /** Extra class on the wrapper, e.g. to let the keys fill a fixed-height layout. */
  className?: string;
}) {
  const push = (d: string) => props.onDigits(appendDigit(props.digits, d));
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
  return (
    <div className={props.className}>
      <div className="sell__keypad" role="group" aria-label="Numeric keypad">
        {keys.map((k) => (
          <button key={k} type="button" className="sell__key" onClick={() => push(k)} aria-label={k}>
            {k}
          </button>
        ))}
        <button type="button" className="sell__key" onClick={() => props.onDigits("")} aria-label="Clear entry">
          C
        </button>
        <button type="button" className="sell__key" onClick={() => push("0")} aria-label="0">
          0
        </button>
        <button
          type="button"
          className="sell__key"
          onClick={() => props.onDigits(props.digits.slice(0, -1))}
          aria-label="Backspace"
        >
          ⌫
        </button>
      </div>
      {props.note ? <p className="sell__note">{props.note}</p> : null}
    </div>
  );
}

function isTextTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || target.tagName === "INPUT" || target.tagName === "TEXTAREA";
}

/**
 * Hardware-keyboard entry for a keypad sheet (D-063), so a tablet with a keyboard or a desktop can type the
 * amount. It listens on the document while mounted rather than using an `<input>`, so a phone never raises
 * its on-screen keyboard and the selling flow stays keypad-only (U-004).
 *
 * Digits (top row or numpad) append, Backspace deletes one, Delete clears, Enter confirms and Escape
 * cancels. "." and "," are ignored: entry is cents-first like the on-screen keys, so "1.50" and "150" both
 * give 1.50. Keys held with Ctrl, Cmd or Alt, and typing into a real text field, pass through untouched.
 */
export function useKeypadKeys(opts: {
  setDigits: Dispatch<SetStateAction<string>>;
  onEnter?: () => void;
  onEscape?: () => void;
}): void {
  const latest = useRef(opts);
  useLayoutEffect(() => {
    latest.current = opts;
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isTextTarget(e.target)) return;
      const { setDigits, onEnter, onEscape } = latest.current;
      const key = e.key;
      if (/^[0-9]$/.test(key)) setDigits((d) => appendDigit(d, key));
      else if (key === "Backspace") setDigits((d) => d.slice(0, -1));
      else if (key === "Delete") setDigits("");
      // Enter would otherwise also "click" whichever keypad button has focus.
      else if (key === "Enter" && onEnter) onEnter();
      else if (key === "Escape" && onEscape) onEscape();
      else return;
      e.preventDefault();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
}

export { keypadDigitsToMinor };
